import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, Polyline } from 'react-leaflet';
import { Segment } from '../types';
import { api } from '../utils/api';

// Fix for default markers in react-leaflet
import L from 'leaflet';
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface SegmentCreatorProps {
  onSegmentCreated?: (segment: Segment) => void;
}

// Custom hook for map click events
function MapClickHandler({ onMapClick }: { onMapClick: (lat: number, lon: number) => void }) {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

const SegmentCreator: React.FC<SegmentCreatorProps> = ({ onSegmentCreated }) => {
  const [startPoint, setStartPoint] = useState<{ lat: number; lon: number } | null>(null);
  const [endPoint, setEndPoint] = useState<{ lat: number; lon: number } | null>(null);
  const [segmentName, setSegmentName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [existingSegments, setExistingSegments] = useState<Segment[]>([]);
  const [selectedSegment, setSelectedSegment] = useState<number | null>(null);

  // Fetch existing segments on component mount
  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const segments = await api.getSegments();
        setExistingSegments(segments);
      } catch (err) {
        console.error('Failed to fetch segments:', err);
      }
    };
    fetchSegments();
  }, []);

  const getRandomColor = (index: number) => {
    const colors = [
      '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
      '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
    ];
    return colors[index % colors.length];
  };

  const handleMapClick = (lat: number, lon: number) => {
    if (!startPoint) {
      setStartPoint({ lat, lon });
      setEndPoint(null);
    } else if (!endPoint) {
      setEndPoint({ lat, lon });
    }
  };

  const resetPoints = () => {
    setStartPoint(null);
    setEndPoint(null);
    setSegmentName('');
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!startPoint || !endPoint || !segmentName.trim()) {
      setError('Please provide a name and select both start and end points');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const newSegment: Omit<Segment, 'id'> = {
        name: segmentName.trim(),
        start_lat: startPoint.lat,
        start_lon: startPoint.lon,
        end_lat: endPoint.lat,
        end_lon: endPoint.lon,
        // Use defaults for radius (100 as per schema)
        radius_start: 100,
        radius_end: 100
      };

      const createdSegment = await api.createSegment(newSegment);
      setSuccess('Segment created successfully!');
      
      // Add the new segment to the existing segments list
      setExistingSegments(prev => [...prev, createdSegment]);
      
      if (onSegmentCreated) {
        onSegmentCreated(createdSegment);
      }
      
      // Reset form after successful creation
      setTimeout(() => {
        resetPoints();
      }, 2000);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create segment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMapCenter = () => {
    if (startPoint && endPoint) {
      return [
        (startPoint.lat + endPoint.lat) / 2,
        (startPoint.lon + endPoint.lon) / 2
      ];
    }
    if (startPoint) {
      return [startPoint.lat, startPoint.lon];
    }
    
    // If no new points, center on existing segments
    if (existingSegments.length > 0) {
      let totalLat = 0;
      let totalLon = 0;
      existingSegments.forEach(segment => {
        totalLat += segment.start_lat;
        totalLon += segment.start_lon;
      });
      return [totalLat / existingSegments.length, totalLon / existingSegments.length];
    }
    
    return [47.6061, -122.3328]; // Default center
  };

  const getMapZoom = () => {
    if (startPoint && endPoint) {
      return 12;
    }
    if (startPoint) {
      return 14;
    }
    
    // Adjust zoom based on existing segments
    if (existingSegments.length === 1) {
      return 14;
    } else if (existingSegments.length <= 5) {
      return 12;
    } else if (existingSegments.length > 5) {
      return 11;
    }
    
    return 13;
  };

  return (
    <div className="bg-slate-300 dark:bg-slate-300 rounded-lg shadow">
      <div className="p-4 border-b">
        <h2 className="text-xl font-semibold text-gray-900">Create Segment</h2>
        <p className="text-gray-600 text-sm mt-1">
          Click on the map to select start and end points for your segment
        </p>
      </div>

      <div className="p-4">
        <form onSubmit={handleSubmit} className="mb-4">
          <div className="mb-4">
            <label htmlFor="segmentName" className="block text-sm font-medium text-gray-700 mb-2">
              Segment Name
            </label>
            <input
              type="text"
              id="segmentName"
              value={segmentName}
              onChange={(e) => setSegmentName(e.target.value)}
              placeholder="Enter segment name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isSubmitting}
            />
          </div>

          <div className="flex gap-2 mb-4">
            <button
              type="submit"
              disabled={!startPoint || !endPoint || !segmentName.trim() || isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {isSubmitting ? 'Creating...' : 'Create Segment'}
            </button>
            
            <button
              type="button"
              onClick={resetPoints}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Reset
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md">
              {success}
            </div>
          )}
        </form>

        <div className="mb-4">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span>Start Point</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span>End Point</span>
            </div>
          </div>
          
          {startPoint && (
            <p className="text-sm text-gray-600 mt-2">
              Start: {startPoint.lat.toFixed(6)}, {startPoint.lon.toFixed(6)}
            </p>
          )}
          
          {endPoint && (
            <p className="text-sm text-gray-600">
              End: {endPoint.lat.toFixed(6)}, {endPoint.lon.toFixed(6)}
            </p>
          )}
        </div>
      </div>

      <div style={{ height: '500px', width: '100%' }}>
        <MapContainer
          center={getMapCenter() as [number, number]}
          zoom={getMapZoom()}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap contributors'
          />
          
          <MapClickHandler onMapClick={handleMapClick} />
          
          {/* Existing segments */}
          {existingSegments.map((segment, index) => {
            const color = getRandomColor(index);
            const isSelected = selectedSegment === segment.id;
            
            return (
              <React.Fragment key={segment.id}>
                {/* Segment line */}
                <Polyline
                  positions={[
                    [segment.start_lat, segment.start_lon],
                    [segment.end_lat, segment.end_lon]
                  ]}
                  color={color}
                  weight={isSelected ? 4 : 2}
                  opacity={isSelected ? 1.0 : 0.6}
                  dashArray="5, 10"
                  eventHandlers={{
                    click: () => setSelectedSegment(segment.id!)
                  }}
                />
                
                {/* Start marker */}
                <Marker
                  position={[segment.start_lat, segment.start_lon]}
                  eventHandlers={{
                    click: () => setSelectedSegment(segment.id!)
                  }}
                >
                  <Popup>
                    <div className="text-sm">
                      <p className="font-semibold text-blue-600">Start: {segment.name}</p>
                      <p>Lat: {segment.start_lat.toFixed(6)}</p>
                      <p>Lon: {segment.start_lon.toFixed(6)}</p>
                    </div>
                  </Popup>
                </Marker>
                
                {/* End marker */}
                <Marker
                  position={[segment.end_lat, segment.end_lon]}
                  eventHandlers={{
                    click: () => setSelectedSegment(segment.id!)
                  }}
                >
                  <Popup>
                    <div className="text-sm">
                      <p className="font-semibold text-red-600">End: {segment.name}</p>
                      <p>Lat: {segment.end_lat.toFixed(6)}</p>
                      <p>Lon: {segment.end_lon.toFixed(6)}</p>
                    </div>
                  </Popup>
                </Marker>
              </React.Fragment>
            );
          })}
          
          {/* New segment start point marker */}
          {startPoint && (
            <Marker position={[startPoint.lat, startPoint.lon]}>
              <Popup>
                <div className="text-sm">
                  <p className="font-semibold text-blue-600">New Start Point</p>
                  <p>Lat: {startPoint.lat.toFixed(6)}</p>
                  <p>Lon: {startPoint.lon.toFixed(6)}</p>
                </div>
              </Popup>
            </Marker>
          )}
          
          {/* New segment end point marker */}
          {endPoint && (
            <Marker position={[endPoint.lat, endPoint.lon]}>
              <Popup>
                <div className="text-sm">
                  <p className="font-semibold text-red-600">New End Point</p>
                  <p>Lat: {endPoint.lat.toFixed(6)}</p>
                  <p>Lon: {endPoint.lon.toFixed(6)}</p>
                </div>
              </Popup>
            </Marker>
          )}
          
          {/* Line connecting new start and end points */}
          {startPoint && endPoint && (
            <Polyline
              positions={[
                [startPoint.lat, startPoint.lon],
                [endPoint.lat, endPoint.lon]
              ]}
              color="#6B7280"
              weight={4}
              opacity={1.0}
              dashArray="10, 5"
            />
          )}
        </MapContainer>
      </div>
      
      {selectedSegment && (
        <div className="p-4 border-t bg-gray-50">
          <p className="text-sm text-gray-600">
            Selected segment: {existingSegments.find(s => s.id === selectedSegment)?.name}
          </p>
        </div>
      )}
    </div>
  );
};

export default SegmentCreator;
